import { Link } from "@tanstack/react-router"

import { AuthBrandPanel } from "@repo/ui/components/bespoke/auth-brand-panel"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/ui/card"
import { Logo } from "@repo/ui/components/ui/logo"
import { cn } from "@repo/ui/lib/utils"

import { AuthPanel } from "./auth-panel"

export type AuthLayoutProps = {
  children: React.ReactNode
  title: string
  footer?: React.ReactNode
  layout?: "centered" | "split"
  formSide?: "left" | "right"
  panel?: "default" | "bespoke"
}

/** Split applies from `lg` up; below it the centered card returns and the panel is hidden. */
export function AuthLayout({
  children,
  title,
  footer,
  layout = "split",
  formSide = "left",
  panel = "default",
}: AuthLayoutProps) {
  const split = layout === "split"

  return (
    <div
      className={cn(
        "bg-background relative flex min-h-dvh w-full flex-col items-center justify-center overflow-hidden p-4 sm:p-6",
        split && "lg:bg-card lg:grid lg:grid-cols-2 lg:items-stretch lg:p-0",
      )}
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,var(--color-muted)_0%,transparent_55%)] opacity-70",
          split && "lg:hidden",
        )}
      />
      <div
        className={cn(
          "relative z-10 flex w-full max-w-md flex-col gap-6",
          split &&
            "lg:max-w-xs lg:justify-center lg:justify-self-center lg:py-12",
          split && formSide === "right" && "lg:order-last",
        )}
      >
        <Link
          to="/"
          className={cn(
            "flex items-center justify-center",
            split && "lg:hidden",
          )}
        >
          <Logo width={100} />
        </Link>
        <Card
          className={cn(
            "py-6",
            split && "lg:overflow-visible lg:border-0 lg:py-0",
          )}
        >
          <CardHeader
            className={cn(
              "flex items-center justify-center pb-4",
              split && "lg:justify-start lg:px-0 lg:pb-6",
            )}
          >
            <CardTitle
              className={cn(
                "text-center",
                split && "lg:text-left lg:text-2xl lg:font-bold",
              )}
            >
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent
            className={cn("flex flex-col gap-6", split && "lg:px-0")}
          >
            {children}
          </CardContent>
          {footer && (
            <CardFooter
              className={cn(
                "justify-center",
                split &&
                  "lg:justify-start lg:border-t-0 lg:bg-transparent lg:px-0",
              )}
            >
              {footer}
            </CardFooter>
          )}
        </Card>
      </div>
      {split && (
        <div className="hidden lg:grid">
          {panel === "bespoke" ? <AuthBrandPanel /> : <AuthPanel />}
        </div>
      )}
    </div>
  )
}
